import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle } from 'lucide-react';
import React from 'react';

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;

    title: string;
    description?: React.ReactNode;
    children?: React.ReactNode; // custom body content

    confirmText?: string;
    cancelText?: string;

    onConfirm: () => void;
    onCancel?: () => void;

    confirmVariant?: 'default' | 'destructive' | 'success';
    loading?: boolean;
    show?:boolean
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    onOpenChange,
    onConfirm,
    onCancel,
    title,
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmVariant = 'destructive',
    loading = false,
    children,
    show = true
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        {title}
                    </DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>


                {/* RENDER PREVIEW AS CHILDREN HERE */}
                {children && <div className="py-4">{children}</div>}
                {/* RENDER PREVIEW AS CHILDREN HERE */}

                {show && <Alert className="border-orange-200 bg-orange-50 mt-4">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                        This action cannot be undone.
                    </AlertDescription>
                </Alert>}


                <DialogFooter>

                    {/* CANCEL BUTTON */}
                    <Button
                        variant="outline"
                        onClick={() => {
                            onCancel?.();          //call your function
                            onOpenChange(false);   //close dialog
                        }}
                        className="border-gray-300 rounded-md"
                        disabled={loading}
                    >
                        {cancelText}
                    </Button>

                    {/* CONFIRM BUTTON */}
                    <Button
                        onClick={onConfirm}  //call your function
                        disabled={loading}
                        className={`rounded-md text-white flex items-center justify-center gap-2 ${confirmVariant === 'destructive'
                            ? 'bg-red-600 hover:bg-red-700'
                            : confirmVariant === 'success'
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        {loading && (
                            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        <span>{loading ? 'Processing...' : confirmText}</span>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmDialog;
